import {
	ERROR_OCCURRED_DURING_LOGOUT,
	LOGIN_COMPLETE,
	LOGIN_FAIL,
	LOGIN_SUCCESS,
	LOGOUT,
	REGISTRATION_DETAIL_COMPLETE,
	SESSION_VALID
} from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';

import getCredentials from '~/components/authentication/get-credentials';
import MOJEID_STATUSES from '~/components/authentication/moje-id/constants';
import { STATUS as CAPTCHA_STATUS } from '~/components/authentication/recaptcha/constants';
import { RESET_PASSWORD_RESET } from '~/components/authentication/reset-password/ducks/action-names';

import * as AN from './action-names';
import preloadedState from './preloaded-state';
import REDUCER_NAME from './reducer-name';

function reducer(state = preloadedState, { type, payload }) {
	switch (type) {
		case AN.SHOW_USERNAME_PASSWORD_LOGIN:
			return {
				...state,
				selectedTab: 'login'
			};
		case AN.SHOW_BANKID_LOGIN:
			return {
				...state,
				selectedTab: 'bankid'
			};
		case AN.SHOW_PAG_LOGIN:
			return {
				...state,
				selectedTab: 'pag-login'
			};
		case AN.SHOW_PHONE_LOGIN:
			return {
				...state,
				selectedTab: 'phone-login'
			};
		case AN.DISABLE_VALIDATE_SESSION:
			return {
				...state,
				isValidateSessionEnabled: false
			};
		case AN.ENABLE_VALIDATE_SESSION:
			return {
				...state,
				isValidateSessionEnabled: true
			};
		case AN.CANCEL_LOGIN:
			return {
				...state,
				showAccountVerification: false,
				showDepositLimits: false,
				shouldShowComplianceMessage: false,
				shouldShowPrivacyPolicyMessage: false
			};
		case AN.UPDATE_CREDENTIALS:
			return { ...state, ...payload };
		case AN.SHOW_DEPOSIT_LIMITS:
			return { ...state, ...payload };
		case LOGIN_SUCCESS: {
			const { ...rest } = payload;
			return {
				...state,
				...rest,
				uiConfig: undefined,
				captchaToken: undefined,
				captchaWhitelisted: undefined,
				captchaStatus: CAPTCHA_STATUS.START
				// authenticated: true // filter out shouldShow
			};
		}
		case LOGIN_COMPLETE:
		case LOGIN_FAIL:
			return {
				...state,
				captchaToken: undefined,
				captchaWhitelisted: undefined,
				loadCaptcha: false,
				...(payload?.result?.email ? { email: payload.result.email } : {})
			};
		case SESSION_VALID:
			return {
				...state,
				authenticated: true,
				sessionExpired: false,
				loadingAuth: false,
				...payload
			};
		case AN.SESSION_EXPIRED:
			return {
				...state,
				sessionExpired: true
			};
		case AN.REGISTRATION_BASIC_START:
		case AN.REGISTRATION_BASIC_FAIL:
		case REGISTRATION_DETAIL_COMPLETE:
			return { ...state, registrationToken: undefined };
		case AN.REGISTRATION_BASIC_SUCCESS:
			return {
				...state,
				registrationToken: payload.value,
				verificationSmsRequired: payload.verificationSmsRequired,
				verificationSmsSent: payload.verificationSmsSent,
				verificationSMSLastSent: 0
			};
		case AN.REGISTRATION_EXTENDED_VERIFICATION:
			return {
				...state,
				showExtendedVerification: payload
			};
		case AN.REGISTRATION_PHONE_RESEND:
			return {
				...state,
				verificationSmsSent: payload.verificationSmsSent,
				verificationSMSLastSent: payload.lastTryDate
			};
		case AN.REGISTRATION_DETAIL_SUCCESS:
			return { ...state, authenticated: payload };
		case LOGOUT:
		case ERROR_OCCURRED_DURING_LOGOUT:
			return {
				...preloadedState,
				email: state.username,
				locale: state.locale,
				authenticated: false,
				sessionExpired: state.sessionExpired,
				// Retain the welcome popup state
				showWelcomePopup: state.showWelcomePopup,
				welcomeOptions: state.welcomeOptions
			};
		case AN.AUTH_LOADING:
			return { ...state, ...payload };
		case RESET_PASSWORD_RESET:
			return { ...state, mustChangePw: false };
		case AN.SHOULD_SHOW_MARKETING_COMMUNICATION_SETTINGS:
			return {
				...state,
				shouldShowMarketingCommunicationSettings: payload.shouldShowMarketingCommunicationSettings
			};
		case AN.SHOW_WELCOME_SCREEN:
			return {
				...state,
				showWelcomeScreen: payload,
				shouldShowWelcomePageDetails: payload // TODO: clean this up, use one flag
			};
		case AN.SHOW_WELCOME_POPUP:
			return {
				...state,
				...payload
			};
		case AN.SET_POST_AUTH_LOCATION:
			return {
				...state,
				postAuthLocation: payload
			};
		case AN.SET_DEVICE_SESSION:
			return {
				...state,
				deviceSession: payload
			};
		case AN.SET_AUTH_EXITPATH:
			return {
				...state,
				exitPath: payload
			};
		case AN.UPDATE_ACCEPT_MARKETING_OFFERS:
			return {
				...state,
				updateAcceptMarketingOffers: payload
			};
		case AN.LOAD_RECAPTCHA:
			return {
				...state,
				loadCaptcha: true
			};
		case AN.LOAD_SEON:
			return {
				...state,
				loadSeon: true
			};
		case AN.RECAPTCHA_EXECUTE:
			return {
				...state,
				captchaWhitelisted: payload.captchaWhitelisted,
				captchaStatus: payload.captchaStatus
			};
		case AN.RECAPTCHA_CALLBACK:
			return {
				...state,
				captchaToken: payload.captchaToken,
				captchaStatus: payload.captchaStatus
			};
		case AN.RECAPTCHA_WHITELIST:
			return {
				...state,
				captchaWhitelisted: true,
				captchaStatus: CAPTCHA_STATUS.COMPLETE
			};
		case AN.RECAPTCHA_RESET:
			return {
				...state,
				captchaToken: '',
				loadCaptcha: false,
				captchaWhitelisted: false,
				captchaStatus: CAPTCHA_STATUS.START
			};
		case AN.SHOW_MOJE_ID:
			return {
				...state,
				showMojeID: payload,
				mojeIDStatus: MOJEID_STATUSES.START
			};
		case AN.MOJEID_STATUS:
			return {
				...state,
				mojeIDStatus: payload
			};
		case AN.SET_CDD_REGISTER_STATUS:
			return {
				...state,
				cddnRegisterStatus: payload
			};
		case AN.TOGGLE_TRANSACTION_LINK:
			return {
				...state,
				showTransactionLinkFlow: payload
			};
		case AN.SHOW_SMS_VERIFICATION:
			return {
				...state,
				...payload
			};
		case AN.MISSING_DATA_SUCCESS:
			return {
				...state,
				...payload
			};
		case AN.CLOSE_LUGAS_LOGIN:
			return {
				...state,
				requiresLoginOnLugas: false
			};
		case AN.TRIGGER_FB_LOGIN:
			return {
				...state,
				tryAgainFB: payload
			};
		case AN.SET_SOCIAL_LOGIN_TYPE:
			return {
				...state,
				socialLoginType: payload
			};
		case AN.SET_SHOULD_SHOW_MARKETING_CONSENT:
			return { ...state, shouldShowMarketingConsent: payload };
		case AN.PERSIST_VALUES:
			return {
				...state,
				persistedValues: {
					values: {
						...state.persistedValues?.values,
						...(payload?.values || {}),
						email:
							payload?.values?.email ||
							payload?.values?.loginEmail ||
							state.persistedValues?.values?.email ||
							'',
						password:
							payload?.values?.password ||
							payload?.values?.loginPassword ||
							state.persistedValues?.values?.password ||
							'',
						phoneNumber:
							payload?.values?.phoneNumber ||
							payload?.values?.loginPhoneNumber ||
							state.persistedValues?.values?.phoneNumber ||
							'',
						callingCode:
							payload?.values?.callingCode ||
							payload?.values?.loginCallingCode ||
							state.persistedValues?.values?.callingCode ||
							''
					},
					errors: { ...state.persistedValues?.errors, ...(payload?.errors || {}) },
					touched: { ...state.persistedValues?.touched, ...(payload?.touched || {}) }
				}
			};
		case AN.CLEAR_PERSISTED_VALUES:
			return { ...state, persistedValues: {} };
		case AN.UPDATE_REGISTRATION_AB_TEST_FLOW:
			return { ...state, ...payload };
		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);

export * from './actions.js';
export { getCredentials };

export default reducer;
